import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const PUBLISHER_JWT_KEY = 'publisher_jwt';
const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';

function getPublisherToken() {
  return localStorage.getItem(PUBLISHER_JWT_KEY) || '';
}

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`color: white;`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 4px;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubTitle = styled.p`
  font-size: 0.82rem;
  color: rgba(255,255,255,0.4);
  margin: 0 0 24px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const PeriodSelect = styled.select`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  color: white;
  font-size: 0.88rem;
  padding: 8px 12px;
  outline: none;
  cursor: pointer;
  &:focus { border-color: rgba(255,71,87,0.5); }
  option { background: #1a1a2e; }
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`;

const KpiCard = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  padding: 20px 18px;
`;

const KpiLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255,255,255,0.35);
  margin-bottom: 8px;
`;

const KpiValue = styled.div`
  font-size: 1.9rem;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const KpiSub = styled.div`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.35);
  margin-top: 4px;
`;

const Section = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 20px 22px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

// ─── Bar chart ────────────────────────────────────────────────────────────────

const ChartWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 80px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const BarCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
`;

const Bar = styled.div`
  width: 18px;
  background: rgba(255,71,87,0.55);
  border-radius: 3px 3px 0 0;
  height: ${p => p.$h}px;
  min-height: 2px;
  transition: height 0.2s;
`;

const BarLabel = styled.div`
  font-size: 0.6rem;
  color: rgba(255,255,255,0.25);
  white-space: nowrap;
  transform: rotate(-45deg);
  transform-origin: top left;
  margin-top: 4px;
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.35);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  cursor: ${p => p.$sortable ? 'pointer' : 'default'};
  user-select: none;
  white-space: nowrap;
  &:hover { color: ${p => p.$sortable ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.35)'}; }
`;

const Td = styled.td`
  padding: 10px 10px;
  color: rgba(255,255,255,0.75);
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:first-child { color: white; }
`;

const PubPill = styled.span`
  font-size: 0.7rem;
  padding: 2px 8px;
  background: rgba(255,71,87,0.1);
  border: 1px solid rgba(255,71,87,0.2);
  color: #ff7c89;
  border-radius: 20px;
  white-space: nowrap;
`;

// ─── Empty / Error states ─────────────────────────────────────────────────────

const EmptyMsg = styled.div`
  text-align: center;
  padding: 48px 0;
  color: rgba(255,255,255,0.3);
  font-size: 0.88rem;
`;

const ErrorMsg = styled.div`
  background: rgba(255,71,87,0.08);
  border: 1px solid rgba(255,71,87,0.2);
  border-radius: 10px;
  padding: 14px 16px;
  color: #ff7c89;
  font-size: 0.85rem;
  margin-bottom: 20px;
`;

// ─── Period helpers ────────────────────────────────────────────────────────────

function buildPeriodOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    opts.push({ val, label });
  }
  return opts;
}

const PERIOD_OPTIONS = buildPeriodOptions();

// Design 039: rolling windows (mirror the Invite Clicks page); shown above the 12 months.
const RANGE_OPTIONS = [
  { val: '7d', label: 'Last 7 days' },
  { val: '30d', label: 'Last 30 days' },
  { val: '90d', label: 'Last 90 days' },
];
const RANGE_VALS = new Set(RANGE_OPTIONS.map(o => o.val));

// ─── Component ────────────────────────────────────────────────────────────────

export default function PublisherAnalyticsDashboard() {
  const [period, setPeriod] = useState('30d'); // design 039: default to the Last 30 days rolling window
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('views');
  const [sortDir, setSortDir] = useState(-1); // -1 = desc

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getPublisherToken();
      const qs = RANGE_VALS.has(period) ? `range=${period}` : `period=${period}`;
      const res = await fetch(`${API_URL}/api/publisher/analytics?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server error (${res.status})`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => -d);
    } else {
      setSortField(field);
      setSortDir(-1);
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDir === -1 ? ' ↓' : ' ↑';
  };

  const sortedVideos = data?.videos ? [...data.videos].sort((a, b) => {
    const av = a[sortField] ?? 0;
    const bv = b[sortField] ?? 0;
    return sortDir * (typeof av === 'string' ? av.localeCompare(bv) : bv - av);
  }) : [];

  // Build bar chart data
  const barData = data?.views_by_day || [];
  const maxViews = Math.max(1, ...barData.map(d => d.views));

  return (
    <Wrap>
      <Title>Analytics</Title>
      <SubTitle>Aggregated view metrics for your publications. No personal data is shown.</SubTitle>

      <Controls>
        <PeriodSelect value={period} onChange={e => setPeriod(e.target.value)}>
          <optgroup label="Rolling window">
            {RANGE_OPTIONS.map(o => (
              <option key={o.val} value={o.val}>{o.label}</option>
            ))}
          </optgroup>
          <optgroup label="Specific month">
            {PERIOD_OPTIONS.map(o => (
              <option key={o.val} value={o.val}>{o.label}</option>
            ))}
          </optgroup>
        </PeriodSelect>
        {loading && <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>Loading…</span>}
      </Controls>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {data && (
        <>
          <KpiRow>
            <KpiCard>
              <KpiLabel>Total Views</KpiLabel>
              <KpiValue>{data.totals.views.toLocaleString()}</KpiValue>
              <KpiSub>times a video started playing</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Unique Viewers</KpiLabel>
              <KpiValue>{data.totals.unique_viewers.toLocaleString()}</KpiValue>
              <KpiSub>different people who watched</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Completions</KpiLabel>
              <KpiValue>{data.totals.completions.toLocaleString()}</KpiValue>
              <KpiSub>watched all the way through</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Avg Watch %</KpiLabel>
              <KpiValue>{data.totals.avg_watch_pct}%</KpiValue>
              <KpiSub>avg % watched, on completed views</KpiSub>
            </KpiCard>
          </KpiRow>

          {data.engagement && (
            <>
              <SectionTitle style={{ margin: '4px 0 4px' }}>Engagement</SectionTitle>
              <SubTitle style={{ margin: '0 0 14px' }}>
                How subscribers move through the reel. People swipe between videos themselves, so a
                high <strong>skip rate</strong> means they're scrolling past rather than watching.
              </SubTitle>
              <KpiRow>
                <KpiCard>
                  <KpiLabel>Skip rate</KpiLabel>
                  <KpiValue>{data.engagement.skip_rate}%</KpiValue>
                  <KpiSub>scrolled past with under ¼ watched</KpiSub>
                </KpiCard>
                <KpiCard>
                  <KpiLabel>Avg watch time</KpiLabel>
                  <KpiValue>{data.engagement.avg_watch_seconds}s</KpiValue>
                  <KpiSub>time on each video (rough estimate)</KpiSub>
                </KpiCard>
                <KpiCard>
                  <KpiLabel>Videos / session</KpiLabel>
                  <KpiValue>{data.engagement.avg_videos_per_session}</KpiValue>
                  <KpiSub>{data.engagement.sessions.toLocaleString()} sessions</KpiSub>
                </KpiCard>
                <KpiCard>
                  <KpiLabel>Avg session</KpiLabel>
                  <KpiValue>{Math.round(data.engagement.avg_session_seconds)}s</KpiValue>
                  <KpiSub>length of a visit, start to finish</KpiSub>
                </KpiCard>
              </KpiRow>
            </>
          )}

          {barData.length > 0 && (
            <Section>
              <SectionTitle>Views by day</SectionTitle>
              <ChartWrap>
                {barData.map(d => (
                  <BarCol key={d.date}>
                    <Bar $h={Math.max(2, Math.round((d.views / maxViews) * 68))} title={`${d.date}: ${d.views}`} />
                    <BarLabel>{d.date.slice(5)}</BarLabel>
                  </BarCol>
                ))}
              </ChartWrap>
            </Section>
          )}

          {data.publications.length > 0 && (
            <Section>
              <SectionTitle>By publication</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <Th>Publication</Th>
                    <Th>Views</Th>
                    <Th>Completions</Th>
                    <Th>Avg watch %</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.publications.map(p => (
                    <tr key={p.publication_id}>
                      <Td>{p.name}</Td>
                      <Td>{p.views.toLocaleString()}</Td>
                      <Td>{p.completions.toLocaleString()}</Td>
                      <Td>{p.avg_watch_pct}%</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Section>
          )}

          <Section>
            <SectionTitle>Top videos</SectionTitle>
            {sortedVideos.length === 0 ? (
              <EmptyMsg>No view data recorded for this period yet.</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th $sortable onClick={() => handleSort('title')}>Title{sortIndicator('title')}</Th>
                    <Th>Publication</Th>
                    <Th $sortable onClick={() => handleSort('views')}>Views{sortIndicator('views')}</Th>
                    <Th $sortable onClick={() => handleSort('completions')}>Completions{sortIndicator('completions')}</Th>
                    <Th $sortable onClick={() => handleSort('avg_watch_pct')}>Avg watch %{sortIndicator('avg_watch_pct')}</Th>
                    <Th $sortable onClick={() => handleSort('skip_rate')}>Skip %{sortIndicator('skip_rate')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVideos.map(v => (
                    <tr key={v.video_id}>
                      <Td>{v.title || v.video_id}</Td>
                      <Td><PubPill>{v.publication_id}</PubPill></Td>
                      <Td>{v.views.toLocaleString()}</Td>
                      <Td>{v.completions.toLocaleString()}</Td>
                      <Td>{v.avg_watch_pct}%</Td>
                      <Td>{v.skip_rate ?? 0}%</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        </>
      )}

      {!loading && !error && !data && (
        <EmptyMsg>Select a period to load analytics.</EmptyMsg>
      )}
    </Wrap>
  );
}
